import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaSearch,
    FaCheck,
    FaCalendarAlt,
    FaExclamationTriangle,
    FaClock,
    FaCheckCircle,
    FaTimes,
    FaBox,
    FaRegCalendarTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
    getProducts,
    updateProductOptionExpiry,
    getProductExpiriesOverview,
    getProductsWithoutExpiry,
    getExpiredProductOptions,
    getExpiredProductOptionsCount,
} from "../../services/ClientService";

const PAGE_SIZE = 12;
const OVERVIEW_PAGE_SIZE = 30;
const UNSET_PAGE_SIZE = 30;
const EXPIRED_PAGE_SIZE = 30;

const notifyExpiryUpdated = () => {
    window.dispatchEvent(new CustomEvent("product-expiry-updated"));
};

const getProductImage = (product) => {
    if (product?.images?.[0]?.preview) return product.images[0].preview;
    const firstOption = product?.productOptions?.[0];
    if (firstOption?.images?.[0]?.preview) return firstOption.images[0].preview;
    try {
        return require("../../../src/no-image-icon-23494.png");
    } catch {
        return "";
    }
};

const formatExpiryDateInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const isValidExpiryDateFormat = (value) => {
    if (!value) return true;
    const match = String(value).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return false;
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || year < 1900) return false;
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const getOptionLabel = (option) => {
    if (option?.combinationLabel) return option.combinationLabel;
    if (typeof option?.weight === "object" && option.weight !== null) {
        return option.weight.weightLabel || `${option.weight.weightValue} ${option.weight.metricLabel || ""}`.trim();
    }
    return option?.weight || "Option";
};

const getExpiryStatus = (expiryDate) => {
    if (!expiryDate || typeof expiryDate !== "string") return { key: "none", label: "No expiry", daysLeft: null };
    const [day, month, year] = expiryDate.split("/").map(Number);
    if (!day || !month || !year) return { key: "none", label: "No expiry", daysLeft: null };
    const exp = new Date(year, month - 1, day);
    const today = new Date();
    exp.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { key: "expired", label: "Expired", daysLeft: diffDays };
    if (diffDays <= 5) return { key: "critical", label: `${diffDays}d left`, daysLeft: diffDays };
    if (diffDays <= 30) return { key: "warning", label: `${diffDays}d left`, daysLeft: diffDays };
    return { key: "good", label: `${diffDays}d left`, daysLeft: diffDays };
};

const statusStyles = {
    expired: { badge: "bg-red-100 text-red-700 ring-red-200", dot: "bg-red-500", icon: FaExclamationTriangle },
    critical: { badge: "bg-red-50 text-red-600 ring-red-100", dot: "bg-red-500", icon: FaExclamationTriangle },
    warning: { badge: "bg-amber-50 text-amber-700 ring-amber-100", dot: "bg-amber-500", icon: FaClock },
    good: { badge: "bg-emerald-50 text-emerald-700 ring-emerald-100", dot: "bg-emerald-500", icon: FaCheckCircle },
    none: { badge: "bg-slate-100 text-slate-500 ring-slate-200", dot: "bg-slate-300", icon: FaCalendarAlt },
};

const ExpiryBadge = ({ expiryDate, size = "sm" }) => {
    const status = getExpiryStatus(expiryDate);
    const style = statusStyles[status.key];
    const Icon = style.icon;
    const sizeClass = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-0.5 text-xs";
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ring-1 ${style.badge} ${sizeClass}`}>
            <Icon className={size === "lg" ? "text-sm" : "text-[10px]"} />
            {expiryDate ? (
                <>
                    <span>{expiryDate}</span>
                    {status.daysLeft !== null && <span className="opacity-75">· {status.label}</span>}
                </>
            ) : (
                <span>No expiry set</span>
            )}
        </span>
    );
};

const rowKey = (row) => `${row.productId}-${row.optionId}`;

const ProductExpiry = () => {
    const [activeTab, setActiveTab] = useState("set");

    // —— Set Expiry tab state ——
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [products, setProducts] = useState([]);
    const [productsSkip, setProductsSkip] = useState(0);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [expiryInput, setExpiryInput] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // —— Expiry not set tab state ——
    const [unsetSearch, setUnsetSearch] = useState("");
    const [debouncedUnsetSearch, setDebouncedUnsetSearch] = useState("");
    const [unsetRows, setUnsetRows] = useState([]);
    const [unsetTotal, setUnsetTotal] = useState(0);
    const [unsetSkip, setUnsetSkip] = useState(0);
    const [hasMoreUnset, setHasMoreUnset] = useState(true);
    const [isLoadingUnset, setIsLoadingUnset] = useState(false);
    const [isFetchingMoreUnset, setIsFetchingMoreUnset] = useState(false);
    const [unsetInputs, setUnsetInputs] = useState({}); // rowKey -> DD/MM/YYYY
    const [unsetSavingKey, setUnsetSavingKey] = useState(null);

    // —— Expired products tab state ——
    const [expiredSearch, setExpiredSearch] = useState("");
    const [debouncedExpiredSearch, setDebouncedExpiredSearch] = useState("");
    const [expiredRows, setExpiredRows] = useState([]);
    const [expiredTotal, setExpiredTotal] = useState(0);
    const [expiredSkip, setExpiredSkip] = useState(0);
    const [hasMoreExpired, setHasMoreExpired] = useState(true);
    const [isLoadingExpired, setIsLoadingExpired] = useState(false);
    const [isFetchingMoreExpired, setIsFetchingMoreExpired] = useState(false);
    const [expiredInputs, setExpiredInputs] = useState({});
    const [expiredSavingKey, setExpiredSavingKey] = useState(null);

    // —— Overview tab state ——
    const [overviewSearch, setOverviewSearch] = useState("");
    const [debouncedOverviewSearch, setDebouncedOverviewSearch] = useState("");
    const [overviewRows, setOverviewRows] = useState([]);
    const [overviewTotal, setOverviewTotal] = useState(0);
    const [overviewSkip, setOverviewSkip] = useState(0);
    const [hasMoreOverview, setHasMoreOverview] = useState(true);
    const [isLoadingOverview, setIsLoadingOverview] = useState(false);
    const [isFetchingMoreOverview, setIsFetchingMoreOverview] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 500);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedUnsetSearch(unsetSearch.trim()), 500);
        return () => clearTimeout(t);
    }, [unsetSearch]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedExpiredSearch(expiredSearch.trim()), 500);
        return () => clearTimeout(t);
    }, [expiredSearch]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedOverviewSearch(overviewSearch.trim()), 500);
        return () => clearTimeout(t);
    }, [overviewSearch]);

    const fetchProductsPage = useCallback(async (skip = 0) => {
        if (skip === 0) {
            setIsLoadingProducts(true);
            setHasMoreProducts(true);
        } else {
            setIsFetchingMore(true);
        }
        try {
            const data = await getProducts({
                searchTerm: debouncedSearch,
                skip,
                limit: PAGE_SIZE,
                isActive: [true, false],
            });
            if (data.success) {
                const fetched = data.data || [];
                if (skip === 0) setProducts(fetched);
                else setProducts((prev) => [...prev, ...fetched]);
                setHasMoreProducts(fetched.length === PAGE_SIZE);
                setProductsSkip(skip + fetched.length);
            } else {
                if (skip === 0) setProducts([]);
                setHasMoreProducts(false);
            }
        } catch {
            if (skip === 0) setProducts([]);
            setHasMoreProducts(false);
        } finally {
            setIsLoadingProducts(false);
            setIsFetchingMore(false);
        }
    }, [debouncedSearch]);

    const fetchUnsetPage = useCallback(async (skip = 0) => {
        if (skip === 0) {
            setIsLoadingUnset(true);
            setHasMoreUnset(true);
        } else {
            setIsFetchingMoreUnset(true);
        }
        try {
            const res = await getProductsWithoutExpiry({
                searchTerm: debouncedUnsetSearch,
                skip,
                limit: UNSET_PAGE_SIZE,
            });
            if (res.success) {
                const fetched = res.data || [];
                if (skip === 0) {
                    setUnsetRows(fetched);
                    setUnsetInputs({});
                } else {
                    setUnsetRows((prev) => [...prev, ...fetched]);
                }
                setUnsetTotal(res.totalCount || 0);
                setHasMoreUnset(fetched.length === UNSET_PAGE_SIZE);
                setUnsetSkip(skip + fetched.length);
            } else {
                if (skip === 0) setUnsetRows([]);
                setHasMoreUnset(false);
            }
        } catch {
            if (skip === 0) setUnsetRows([]);
            setHasMoreUnset(false);
        } finally {
            setIsLoadingUnset(false);
            setIsFetchingMoreUnset(false);
        }
    }, [debouncedUnsetSearch]);

    const fetchExpiredPage = useCallback(async (skip = 0) => {
        if (skip === 0) {
            setIsLoadingExpired(true);
            setHasMoreExpired(true);
        } else {
            setIsFetchingMoreExpired(true);
        }
        try {
            const res = await getExpiredProductOptions({
                searchTerm: debouncedExpiredSearch,
                skip,
                limit: EXPIRED_PAGE_SIZE,
            });
            if (res.success) {
                const fetched = res.data || [];
                if (skip === 0) {
                    setExpiredRows(fetched);
                    setExpiredInputs(
                        Object.fromEntries(fetched.map((row) => [rowKey(row), row.expiryDate || ""]))
                    );
                } else {
                    setExpiredRows((prev) => [...prev, ...fetched]);
                    setExpiredInputs((prev) => {
                        const next = { ...prev };
                        fetched.forEach((row) => {
                            const key = rowKey(row);
                            if (!(key in next)) next[key] = row.expiryDate || "";
                        });
                        return next;
                    });
                }
                setExpiredTotal(res.totalCount || 0);
                setHasMoreExpired(fetched.length === EXPIRED_PAGE_SIZE);
                setExpiredSkip(skip + fetched.length);
            } else {
                if (skip === 0) setExpiredRows([]);
                setHasMoreExpired(false);
            }
        } catch {
            if (skip === 0) setExpiredRows([]);
            setHasMoreExpired(false);
        } finally {
            setIsLoadingExpired(false);
            setIsFetchingMoreExpired(false);
        }
    }, [debouncedExpiredSearch]);

    const fetchOverviewPage = useCallback(async (skip = 0) => {
        if (skip === 0) {
            setIsLoadingOverview(true);
            setHasMoreOverview(true);
        } else {
            setIsFetchingMoreOverview(true);
        }
        try {
            const res = await getProductExpiriesOverview({
                searchTerm: debouncedOverviewSearch,
                skip,
                limit: OVERVIEW_PAGE_SIZE,
            });
            if (res.success) {
                const fetched = res.data || [];
                if (skip === 0) setOverviewRows(fetched);
                else setOverviewRows((prev) => [...prev, ...fetched]);
                setOverviewTotal(res.totalCount || 0);
                setHasMoreOverview(fetched.length === OVERVIEW_PAGE_SIZE);
                setOverviewSkip(skip + fetched.length);
            } else {
                if (skip === 0) setOverviewRows([]);
                setHasMoreOverview(false);
            }
        } catch {
            if (skip === 0) setOverviewRows([]);
            setHasMoreOverview(false);
        } finally {
            setIsLoadingOverview(false);
            setIsFetchingMoreOverview(false);
        }
    }, [debouncedOverviewSearch]);

    useEffect(() => {
        const syncExpiredCount = async () => {
            try {
                const res = await getExpiredProductOptionsCount({});
                if (res?.success) {
                    setExpiredTotal(Number(res.data?.count) || 0);
                }
            } catch {
                /* non-blocking */
            }
        };
        syncExpiredCount();
        window.addEventListener("product-expiry-updated", syncExpiredCount);
        return () => window.removeEventListener("product-expiry-updated", syncExpiredCount);
    }, []);

    useEffect(() => {
        if (activeTab !== "set") return;
        setProductsSkip(0);
        fetchProductsPage(0);
    }, [debouncedSearch, activeTab, fetchProductsPage]);

    useEffect(() => {
        if (activeTab !== "unset") return;
        setUnsetSkip(0);
        fetchUnsetPage(0);
    }, [debouncedUnsetSearch, activeTab, fetchUnsetPage]);

    useEffect(() => {
        if (activeTab !== "expired") return;
        setExpiredSkip(0);
        fetchExpiredPage(0);
    }, [debouncedExpiredSearch, activeTab, fetchExpiredPage]);

    useEffect(() => {
        if (activeTab !== "overview") return;
        setOverviewSkip(0);
        fetchOverviewPage(0);
    }, [debouncedOverviewSearch, activeTab, fetchOverviewPage]);

    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
        const firstOption = product.productOptions?.[0];
        if (firstOption) {
            setSelectedOptionId(String(firstOption._id));
            setExpiryInput(firstOption.expiryDate || "");
        } else {
            setSelectedOptionId(null);
            setExpiryInput("");
        }
    };

    const handleSelectOption = (option) => {
        setSelectedOptionId(String(option._id));
        setExpiryInput(option.expiryDate || "");
    };

    const refreshSelectedProduct = async () => {
        if (!selectedProduct?._id) return;
        try {
            const res = await getProducts({ _id: selectedProduct._id, limit: 1 });
            if (res.success && res.data?.[0]) {
                setSelectedProduct(res.data[0]);
                const updatedOption = res.data[0].productOptions?.find(
                    (o) => String(o._id) === String(selectedOptionId)
                );
                if (updatedOption) setExpiryInput(updatedOption.expiryDate || "");
            }
        } catch {
            /* non-blocking */
        }
    };

    const handleSaveExpiry = async () => {
        if (!selectedProduct || !selectedOptionId) {
            toast.error("Select a product and option first");
            return;
        }
        if (!isValidExpiryDateFormat(expiryInput)) {
            toast.error("Expiry date must be valid DD/MM/YYYY format");
            return;
        }
        setSubmitting(true);
        try {
            const res = await updateProductOptionExpiry({
                productId: selectedProduct._id,
                optionId: selectedOptionId,
                expiryDate: expiryInput.trim(),
            });
            if (res.success) {
                toast.success(res.message || "Expiry date saved");
                await refreshSelectedProduct();
                setProducts((prev) =>
                    prev.map((p) => {
                        if (String(p._id) !== String(selectedProduct._id)) return p;
                        return {
                            ...p,
                            productOptions: (p.productOptions || []).map((o) =>
                                String(o._id) === String(selectedOptionId)
                                    ? { ...o, expiryDate: expiryInput.trim() || "" }
                                    : o
                            ),
                        };
                    })
                );
                if (overviewRows.length > 0 || activeTab === "overview") {
                    fetchOverviewPage(0);
                }
                if (unsetRows.length > 0 || activeTab === "unset") {
                    fetchUnsetPage(0);
                }
                if (expiredRows.length > 0 || activeTab === "expired") {
                    fetchExpiredPage(0);
                }
                notifyExpiryUpdated();
            } else {
                toast.error(res.message || res.error || "Could not save expiry date");
            }
        } catch (err) {
            toast.error(err?.message || "Could not save expiry date");
        } finally {
            setSubmitting(false);
        }
    };

    const handleClearExpiry = async () => {
        if (!selectedProduct || !selectedOptionId) return;
        setExpiryInput("");
        setSubmitting(true);
        try {
            const res = await updateProductOptionExpiry({
                productId: selectedProduct._id,
                optionId: selectedOptionId,
                expiryDate: "",
            });
            if (res.success) {
                toast.success("Expiry date cleared");
                await refreshSelectedProduct();
                fetchOverviewPage(0);
                fetchUnsetPage(0);
                fetchExpiredPage(0);
                notifyExpiryUpdated();
            } else {
                toast.error(res.message || "Could not clear expiry date");
            }
        } catch (err) {
            toast.error(err?.message || "Could not clear expiry date");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveUnsetExpiry = async (row) => {
        const key = rowKey(row);
        const value = String(unsetInputs[key] || "").trim();
        if (!value) {
            toast.error("Enter an expiry date (DD/MM/YYYY)");
            return;
        }
        if (!isValidExpiryDateFormat(value)) {
            toast.error("Expiry date must be valid DD/MM/YYYY format");
            return;
        }
        setUnsetSavingKey(key);
        try {
            const res = await updateProductOptionExpiry({
                productId: row.productId,
                optionId: row.optionId,
                expiryDate: value,
            });
            if (res.success) {
                toast.success(res.message || "Expiry date saved");
                setUnsetRows((prev) => prev.filter((r) => rowKey(r) !== key));
                setUnsetTotal((t) => Math.max(0, t - 1));
                setUnsetInputs((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
                if (overviewRows.length > 0 || activeTab === "overview") {
                    fetchOverviewPage(0);
                }
                if (expiredRows.length > 0 || activeTab === "expired") {
                    fetchExpiredPage(0);
                }
                notifyExpiryUpdated();
            } else {
                toast.error(res.message || res.error || "Could not save expiry date");
            }
        } catch (err) {
            toast.error(err?.message || "Could not save expiry date");
        } finally {
            setUnsetSavingKey(null);
        }
    };

    const handleSaveExpiredExpiry = async (row) => {
        const key = rowKey(row);
        const value = String(expiredInputs[key] || "").trim();
        if (!value) {
            toast.error("Enter an expiry date (DD/MM/YYYY)");
            return;
        }
        if (!isValidExpiryDateFormat(value)) {
            toast.error("Expiry date must be valid DD/MM/YYYY format");
            return;
        }
        setExpiredSavingKey(key);
        try {
            const res = await updateProductOptionExpiry({
                productId: row.productId,
                optionId: row.optionId,
                expiryDate: value,
            });
            if (res.success) {
                toast.success(res.message || "Expiry date saved");
                const stillExpired = getExpiryStatus(value).key === "expired";
                if (stillExpired) {
                    setExpiredRows((prev) =>
                        prev.map((r) => (rowKey(r) === key ? { ...r, expiryDate: value } : r))
                    );
                } else {
                    setExpiredRows((prev) => prev.filter((r) => rowKey(r) !== key));
                    setExpiredTotal((t) => Math.max(0, t - 1));
                    setExpiredInputs((prev) => {
                        const next = { ...prev };
                        delete next[key];
                        return next;
                    });
                }
                if (overviewRows.length > 0 || activeTab === "overview") {
                    fetchOverviewPage(0);
                }
                notifyExpiryUpdated();
            } else {
                toast.error(res.message || res.error || "Could not save expiry date");
            }
        } catch (err) {
            toast.error(err?.message || "Could not save expiry date");
        } finally {
            setExpiredSavingKey(null);
        }
    };

    const handleJumpToProduct = async (row, { openUnsetInput, openExpiredInput } = {}) => {
        setActiveTab("set");
        try {
            const res = await getProducts({ _id: row.productId, limit: 1 });
            if (res.success && res.data?.[0]) {
                const product = res.data[0];
                setSelectedProduct(product);
                setSelectedOptionId(String(row.optionId));
                setExpiryInput(
                    openUnsetInput
                        ? String(unsetInputs[rowKey(row)] || "")
                        : openExpiredInput
                            ? String(expiredInputs[rowKey(row)] || row.expiryDate || "")
                            : row.expiryDate || ""
                );
            }
        } catch {
            toast.error("Could not load product");
        }
    };

    const selectedOption = selectedProduct?.productOptions?.find(
        (o) => String(o._id) === String(selectedOptionId)
    );

    const criticalCount = overviewRows.filter((r) => {
        const s = getExpiryStatus(r.expiryDate);
        return s.key === "expired" || s.key === "critical";
    }).length;

    const tabs = [
        { id: "set", label: "Set Expiry", icon: FaCalendarAlt },
        { id: "expired", label: "Expired Products", icon: FaExclamationTriangle },
        { id: "unset", label: "Expiry not set", icon: FaRegCalendarTimes },
        { id: "overview", label: "Expiry Overview", icon: FaBox },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50/60 via-white to-orange-50/40 p-4 md:p-8">
            <div className="max-w-6xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <div className="flex items-center gap-4">
                        <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-200">
                            <FaCalendarAlt className="text-2xl" />
                        </div>
                         <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Product Expiry</h1>
                    </div>
                </motion.div>

                {/* Tab switcher */}
                <div className="flex flex-wrap gap-2 mb-6 p-1 bg-white/80 backdrop-blur rounded-2xl border border-slate-200/80 shadow-sm w-fit">
                    {tabs.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setActiveTab(id)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                                activeTab === id
                                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md"
                                    : "text-slate-600 hover:bg-slate-50"
                            }`}
                        >
                            <Icon />
                            {label}
                            {id === "expired" && expiredTotal > 0 && (
                                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    activeTab === id ? "bg-white/25" : "bg-red-100 text-red-700"
                                }`}>
                                    {expiredTotal}
                                </span>
                            )}
                            {id === "unset" && unsetTotal > 0 && (
                                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    activeTab === id ? "bg-white/25" : "bg-slate-200 text-slate-700"
                                }`}>
                                    {unsetTotal}
                                </span>
                            )}
                            {id === "overview" && overviewTotal > 0 && (
                                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    activeTab === id ? "bg-white/25" : "bg-amber-100 text-amber-700"
                                }`}>
                                    {overviewTotal}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    {activeTab === "set" && (
                        <motion.div
                            key="set"
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 12 }}
                            className="grid grid-cols-1 lg:grid-cols-5 gap-6"
                        >
                            {/* Product search panel */}
                            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Search products</label>
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none transition"
                                        placeholder="Name or product ID…"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div
                                    className="mt-4 h-[480px] overflow-y-auto space-y-2 pr-1 scrollbar-thin"
                                    onScroll={(e) => {
                                        const el = e.currentTarget;
                                        if (
                                            el.scrollTop + el.clientHeight >= el.scrollHeight - 50 &&
                                            !isFetchingMore && !isLoadingProducts && hasMoreProducts
                                        ) {
                                            fetchProductsPage(productsSkip);
                                        }
                                    }}
                                >
                                    {isLoadingProducts && productsSkip === 0 ? (
                                        <div className="flex items-center justify-center h-full min-h-[200px]">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
                                        </div>
                                    ) : products.length === 0 ? (
                                        <p className="text-sm text-slate-500 p-4 text-center">No products match this search.</p>
                                    ) : (
                                        <>
                                            {products.map((p) => (
                                                <button
                                                    type="button"
                                                    key={p._id}
                                                    onClick={() => handleSelectProduct(p)}
                                                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                                        selectedProduct?._id === p._id
                                                            ? "border-amber-400 bg-amber-50/80 ring-2 ring-amber-200 shadow-sm"
                                                            : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    <img src={getProductImage(p)} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100 shrink-0" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="font-medium text-slate-900 truncate">{p.productName}</div>
                                                        <div className="text-xs text-slate-500 truncate">{p.productId}</div>
                                                        <div className="text-[10px] text-slate-400 mt-0.5">
                                                            {(p.productOptions || []).length} option{(p.productOptions || []).length !== 1 ? "s" : ""}
                                                        </div>
                                                    </div>
                                                    {selectedProduct?._id === p._id && <FaCheck className="text-amber-600 shrink-0" />}
                                                </button>
                                            ))}
                                            {isFetchingMore && (
                                                <div className="flex justify-center py-3">
                                                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Option & expiry panel */}
                            <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-slate-200/80 p-5 flex flex-col min-h-[560px]">
                                {!selectedProduct ? (
                                    <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                                        <div className="h-16 w-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
                                            <FaBox className="text-2xl text-amber-400" />
                                        </div>
                                        <p className="text-slate-500 text-sm max-w-xs">
                                            Select a product from the list to choose an option and set its expiry date.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex gap-4 mb-6 pb-5 border-b border-slate-100">
                                            <img
                                                src={getProductImage(selectedProduct)}
                                                alt=""
                                                className="w-16 h-16 rounded-xl object-cover bg-slate-100 shrink-0 ring-2 ring-amber-100"
                                            />
                                            <div className="min-w-0">
                                                <h2 className="font-bold text-lg text-slate-900 leading-snug">{selectedProduct.productName}</h2>
                                                <p className="text-xs text-slate-500 mt-0.5">{selectedProduct.productId}</p>
                                            </div>
                                        </div>

                                        <p className="text-sm font-semibold text-slate-700 mb-3">Select product option</p>
                                        <div className="flex flex-wrap gap-2 mb-6">
                                            {(selectedProduct.productOptions || []).map((option) => {
                                                const isSelected = String(option._id) === String(selectedOptionId);
                                                const status = getExpiryStatus(option.expiryDate);
                                                const dotColor = statusStyles[status.key].dot;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={option._id}
                                                        onClick={() => handleSelectOption(option)}
                                                        className={`group relative flex flex-col items-start gap-1 px-4 py-3 rounded-xl border text-left transition-all min-w-[120px] ${
                                                            isSelected
                                                                ? "border-amber-400 bg-amber-50 ring-2 ring-amber-200"
                                                                : "border-slate-200 hover:border-amber-200 hover:bg-amber-50/40"
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2 w-full">
                                                            <span className={`h-2 w-2 rounded-full shrink-0 ${dotColor}`} />
                                                            <span className="font-medium text-sm text-slate-800 truncate">{getOptionLabel(option)}</span>
                                                        </div>
                                                        {option.expiryDate ? (
                                                            <span className="text-[11px] text-slate-500 pl-4">{option.expiryDate}</span>
                                                        ) : (
                                                            <span className="text-[11px] text-slate-400 pl-4 italic">No expiry</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {selectedOption && (
                                            <div className="rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50/80 to-orange-50/40 p-5 mb-6">
                                                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700/80">Current expiry</p>
                                                        <div className="mt-1">
                                                            <ExpiryBadge expiryDate={selectedOption.expiryDate} size="lg" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    New expiry date <span className="font-normal text-slate-400">(DD/MM/YYYY)</span>
                                                </label>
                                                <div className="flex gap-2">
                                                    <div className="relative flex-1">
                                                        <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-400" />
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. 31/12/2026"
                                                            value={expiryInput}
                                                            onChange={(e) => setExpiryInput(formatExpiryDateInput(e.target.value))}
                                                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-amber-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none text-slate-800 font-medium"
                                                        />
                                                    </div>
                                                </div>
                                                <p className="text-xs text-slate-500 mt-2">
                                                    Type digits only — slashes are added automatically. Leave empty and clear to remove expiry.
                                                </p>
                                            </div>
                                        )}

                                        <div className="mt-auto flex gap-3">
                                            {selectedOption?.expiryDate && (
                                                <button
                                                    type="button"
                                                    disabled={submitting}
                                                    onClick={handleClearExpiry}
                                                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 transition"
                                                >
                                                    <FaTimes />
                                                    Clear
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                disabled={submitting || !selectedOptionId}
                                                onClick={handleSaveExpiry}
                                                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-md shadow-amber-200/50"
                                            >
                                                {submitting ? "Saving…" : "Save expiry date"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === "expired" && (
                        <motion.div
                            key="expired"
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -12 }}
                            className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden"
                        >
                            <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-red-50/80 to-orange-50/40">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h2 className="font-bold text-slate-900">Expired Products</h2>
                                        <p className="text-sm text-slate-500 mt-0.5">
                                            {expiredTotal} option{expiredTotal !== 1 ? "s" : ""} past expiry — update dates here
                                        </p>
                                    </div>
                                    <div className="relative w-full sm:w-72">
                                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-400 outline-none text-sm"
                                            placeholder="Filter by product…"
                                            value={expiredSearch}
                                            onChange={(e) => setExpiredSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div
                                className="max-h-[600px] overflow-y-auto"
                                onScroll={(e) => {
                                    const el = e.currentTarget;
                                    if (
                                        el.scrollTop + el.clientHeight >= el.scrollHeight - 80 &&
                                        !isFetchingMoreExpired && !isLoadingExpired && hasMoreExpired
                                    ) {
                                        fetchExpiredPage(expiredSkip);
                                    }
                                }}
                            >
                                {isLoadingExpired && expiredSkip === 0 ? (
                                    <div className="flex items-center justify-center py-20">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
                                    </div>
                                ) : expiredRows.length === 0 ? (
                                    <div className="text-center py-16 px-6">
                                        <FaCheckCircle className="text-4xl text-emerald-300 mx-auto mb-3" />
                                        <p className="text-slate-500 text-sm">No expired product options.</p>
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab("overview")}
                                            className="mt-4 text-sm font-semibold text-amber-600 hover:text-amber-700"
                                        >
                                            View Expiry Overview →
                                        </button>
                                    </div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                            <tr>
                                                <th className="text-left px-5 py-3">Product</th>
                                                <th className="text-left px-5 py-3">Option</th>
                                                <th className="text-left px-5 py-3">Current expiry</th>
                                                <th className="text-left px-5 py-3">New expiry (DD/MM/YYYY)</th>
                                                <th className="px-5 py-3" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {expiredRows.map((row) => {
                                                const key = rowKey(row);
                                                const value = expiredInputs[key] ?? row.expiryDate ?? "";
                                                const saving = expiredSavingKey === key;
                                                const status = getExpiryStatus(row.expiryDate);
                                                const style = statusStyles[status.key];
                                                return (
                                                    <tr
                                                        key={key}
                                                        className="hover:bg-red-50/30 transition-colors"
                                                    >
                                                        <td className="px-5 py-3.5">
                                                            <div className="font-medium text-slate-900">{row.productName}</div>
                                                            <div className="text-xs text-slate-400">{row.productCode}</div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-slate-700">{row.optionLabel}</td>
                                                        <td className="px-5 py-3.5">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${style.badge}`}>
                                                                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                                                                {row.expiryDate}
                                                            </span>
                                                        </td>
                                                        <td className="px-5 py-3.5">
                                                            <div className="relative max-w-[180px]">
                                                                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                                                                <input
                                                                    type="text"
                                                                    placeholder="31/12/2026"
                                                                    value={value}
                                                                    onChange={(e) =>
                                                                        setExpiredInputs((prev) => ({
                                                                            ...prev,
                                                                            [key]: formatExpiryDateInput(e.target.value),
                                                                        }))
                                                                    }
                                                                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none text-sm font-medium"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right whitespace-nowrap">
                                                            <button
                                                                type="button"
                                                                disabled={saving || !value.trim()}
                                                                onClick={() => handleSaveExpiredExpiry(row)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                                            >
                                                                {saving ? "Saving…" : "Save"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleJumpToProduct(row, { openExpiredInput: true })}
                                                                className="ml-2 text-xs font-semibold text-slate-500 hover:text-amber-700 transition"
                                                            >
                                                                Open
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                                {isFetchingMoreExpired && (
                                    <div className="flex justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === "unset" && (
                        <motion.div
                            key="unset"
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -12 }}
                            className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden"
                        >
                            <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100/50">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h2 className="font-bold text-slate-900">Expiry not set</h2>
                                        <p className="text-sm text-slate-500 mt-0.5">
                                            {unsetTotal} option{unsetTotal !== 1 ? "s" : ""} without an expiry date — set them here
                                        </p>
                                    </div>
                                    <div className="relative w-full sm:w-72">
                                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-400 outline-none text-sm"
                                            placeholder="Filter by product…"
                                            value={unsetSearch}
                                            onChange={(e) => setUnsetSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div
                                className="max-h-[600px] overflow-y-auto"
                                onScroll={(e) => {
                                    const el = e.currentTarget;
                                    if (
                                        el.scrollTop + el.clientHeight >= el.scrollHeight - 80 &&
                                        !isFetchingMoreUnset && !isLoadingUnset && hasMoreUnset
                                    ) {
                                        fetchUnsetPage(unsetSkip);
                                    }
                                }}
                            >
                                {isLoadingUnset && unsetSkip === 0 ? (
                                    <div className="flex items-center justify-center py-20">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
                                    </div>
                                ) : unsetRows.length === 0 ? (
                                    <div className="text-center py-16 px-6">
                                        <FaCheckCircle className="text-4xl text-emerald-300 mx-auto mb-3" />
                                        <p className="text-slate-500 text-sm">All product options have an expiry date set.</p>
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab("overview")}
                                            className="mt-4 text-sm font-semibold text-amber-600 hover:text-amber-700"
                                        >
                                            View Expiry Overview →
                                        </button>
                                    </div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                            <tr>
                                                <th className="text-left px-5 py-3">Product</th>
                                                <th className="text-left px-5 py-3">Option</th>
                                                <th className="text-left px-5 py-3">Set expiry (DD/MM/YYYY)</th>
                                                <th className="px-5 py-3" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {unsetRows.map((row) => {
                                                const key = rowKey(row);
                                                const value = unsetInputs[key] || "";
                                                const saving = unsetSavingKey === key;
                                                return (
                                                    <tr
                                                        key={key}
                                                        className="hover:bg-slate-50/80 transition-colors"
                                                    >
                                                        <td className="px-5 py-3.5">
                                                            <div className="font-medium text-slate-900">{row.productName}</div>
                                                            <div className="text-xs text-slate-400">{row.productCode}</div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-slate-700">{row.optionLabel}</td>
                                                        <td className="px-5 py-3.5">
                                                            <div className="relative max-w-[180px]">
                                                                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                                                                <input
                                                                    type="text"
                                                                    placeholder="31/12/2026"
                                                                    value={value}
                                                                    onChange={(e) =>
                                                                        setUnsetInputs((prev) => ({
                                                                            ...prev,
                                                                            [key]: formatExpiryDateInput(e.target.value),
                                                                        }))
                                                                    }
                                                                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-400 focus:border-amber-400 outline-none text-sm font-medium"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right whitespace-nowrap">
                                                            <button
                                                                type="button"
                                                                disabled={saving || !value.trim()}
                                                                onClick={() => handleSaveUnsetExpiry(row)}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                                            >
                                                                {saving ? "Saving…" : "Save"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleJumpToProduct(row, { openUnsetInput: true })}
                                                                className="ml-2 text-xs font-semibold text-slate-500 hover:text-amber-700 transition"
                                                            >
                                                                Open
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                                {isFetchingMoreUnset && (
                                    <div className="flex justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {activeTab === "overview" && (
                        <motion.div
                            key="overview"
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -12 }}
                            className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden"
                        >
                            <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-amber-50/30">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <h2 className="font-bold text-slate-900">All set expiry dates</h2>
                                        <p className="text-sm text-slate-500 mt-0.5">
                                            {overviewTotal} option{overviewTotal !== 1 ? "s" : ""} with expiry
                                            {criticalCount > 0 && (
                                                <span className="ml-2 text-red-600 font-medium">
                                                    · {criticalCount} urgent
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="relative w-full sm:w-72">
                                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-400 outline-none text-sm"
                                            placeholder="Filter by product…"
                                            value={overviewSearch}
                                            onChange={(e) => setOverviewSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div
                                className="max-h-[600px] overflow-y-auto"
                                onScroll={(e) => {
                                    const el = e.currentTarget;
                                    if (
                                        el.scrollTop + el.clientHeight >= el.scrollHeight - 80 &&
                                        !isFetchingMoreOverview && !isLoadingOverview && hasMoreOverview
                                    ) {
                                        fetchOverviewPage(overviewSkip);
                                    }
                                }}
                            >
                                {isLoadingOverview && overviewSkip === 0 ? (
                                    <div className="flex items-center justify-center py-20">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
                                    </div>
                                ) : overviewRows.length === 0 ? (
                                    <div className="text-center py-16 px-6">
                                        <FaCalendarAlt className="text-4xl text-slate-200 mx-auto mb-3" />
                                        <p className="text-slate-500 text-sm">No expiry dates set yet.</p>
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab("unset")}
                                            className="mt-4 text-sm font-semibold text-amber-600 hover:text-amber-700"
                                        >
                                            See options without expiry →
                                        </button>
                                    </div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                            <tr>
                                                <th className="text-left px-5 py-3">Product</th>
                                                <th className="text-left px-5 py-3">Option</th>
                                                <th className="text-left px-5 py-3">Expiry</th>
                                                <th className="text-left px-5 py-3">Status</th>
                                                <th className="px-5 py-3" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {overviewRows.map((row) => {
                                                const status = getExpiryStatus(row.expiryDate);
                                                const style = statusStyles[status.key];
                                                return (
                                                    <tr
                                                        key={`${row.productId}-${row.optionId}`}
                                                        className="hover:bg-amber-50/30 transition-colors"
                                                    >
                                                        <td className="px-5 py-3.5">
                                                            <div className="font-medium text-slate-900">{row.productName}</div>
                                                            <div className="text-xs text-slate-400">{row.productCode}</div>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-slate-700">{row.optionLabel}</td>
                                                        <td className="px-5 py-3.5 font-mono font-medium text-slate-800">{row.expiryDate}</td>
                                                        <td className="px-5 py-3.5">
                                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${style.badge}`}>
                                                                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                                                                {status.label}
                                                            </span>
                                                        </td>
                                                        <td className="px-5 py-3.5 text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleJumpToProduct(row)}
                                                                className="text-xs font-semibold text-amber-600 hover:text-amber-800 transition"
                                                            >
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                                {isFetchingMoreOverview && (
                                    <div className="flex justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ProductExpiry;
